import { LoadingState } from "@/components/ui/loading-state";
import { useRef, useState } from "react";
import { Sparkles, X } from "lucide-react";

export interface ChatMessage {
  id: number | string;
  role: "assistant" | "user";
  text: string;
}

interface BuilderStageChatProps {
  title: string;
  intro?: string;
  placeholder: string;
  variant?: "floating" | "inline" | "docked";
  disabled?: boolean;
  onSend?: (comment: string) => Promise<string>;
  pendingLabel?: string;
  errorMessage?: string;
  messages?: readonly ChatMessage[];
  historyLoading?: boolean;
  historyError?: string;
  requestPending?: boolean;
  requestError?: string;
  contextLabel?: string;
  onClearContext?: () => void;
}

export function BuilderStageChat({
  title,
  intro,
  placeholder,
  variant = "floating",
  disabled = false,
  onSend,
  pendingLabel = "Reviewing the Basis…",
  errorMessage = "I couldn't process that comment. Please try again.",
  messages: persistedMessages,
  historyLoading = false,
  historyError,
  requestPending = false,
  requestError,
  contextLabel,
  onClearContext,
}: BuilderStageChatProps) {
  const [open, setOpen] = useState(variant === "inline");
  const [draft, setDraft] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>(
    intro ? [{ id: 0, role: "assistant", text: intro }] : [],
  );
  const [sending, setSending] = useState(false);
  const [pendingUserMessage, setPendingUserMessage] =
    useState<ChatMessage | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const nextMessageId = useRef(1);
  const controlled = persistedMessages !== undefined;

  const send = async () => {
    const text = draft.trim();
    if (!text || sending || disabled || historyLoading || historyError) return;
    const userMessage: ChatMessage = {
      id: nextMessageId.current++,
      role: "user",
      text,
    };
    if (controlled) {
      setPendingUserMessage(userMessage);
      setSendError(null);
    } else {
      setMessages((current) => [...current, userMessage]);
    }
    setDraft("");
    setSending(true);
    try {
      const reply = onSend
        ? await onSend(text)
        : (
          "Got it. This chat is currently a UI preview and is not connected "
          + "to project changes yet."
        );
      if (!controlled) {
        setMessages((current) => [
          ...current,
          {
            id: nextMessageId.current++,
            role: "assistant",
            text: reply,
          },
        ]);
      }
    } catch {
      if (controlled) {
        setSendError(errorMessage);
      } else {
        setMessages((current) => [
          ...current,
          {
            id: nextMessageId.current++,
            role: "assistant",
            text: errorMessage,
          },
        ]);
      }
    } finally {
      setPendingUserMessage(null);
      setSending(false);
    }
  };

  const launcher = (
    <button
      type="button"
      onClick={() => setOpen((current) => !current)}
      aria-expanded={open}
      className="flex items-center gap-1.5 rounded-full bg-foreground py-2.5 pl-3 pr-4 font-medium text-background shadow-lg hover:bg-foreground/90"
      style={{ fontSize: 11.5 }}
    >
      <Sparkles aria-hidden="true" size={13} />
      Ask Dilli
    </button>
  );

  if (!open && variant === "floating") {
    return (
      <div className="fixed bottom-5 right-5 z-40">{launcher}</div>
    );
  }

  const visibleMessages = controlled
    ? [
        ...(intro
          ? [{ id: "intro", role: "assistant" as const, text: intro }]
          : []),
        ...persistedMessages,
        ...(pendingUserMessage ? [pendingUserMessage] : []),
      ]
    : messages;
  const inputDisabled = (
    disabled || sending || requestPending || historyLoading || Boolean(historyError)
  );

  const chat = (
    <>
      <div className="flex items-center justify-between gap-2 border-b border-border/60 px-3 py-2">
        <span
          className="flex min-w-0 items-center gap-1.5 truncate font-semibold"
          style={{ fontSize: 11.5 }}
        >
          <Sparkles aria-hidden="true" className="shrink-0" size={12} />
          {title}
        </span>
        {variant !== "inline" && (
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close chat"
            className="shrink-0 p-1 text-muted-foreground/70 hover:text-foreground"
          >
            <X aria-hidden="true" size={13} />
          </button>
        )}
      </div>
      <div
        aria-live="polite"
        className="flex-1 space-y-2 overflow-y-auto bg-background/50 p-3"
      >
        {visibleMessages.map((message) => (
          <div
            key={message.id}
            className={message.role === "user" ? "flex justify-end" : "flex"}
          >
            <div
              className={`max-w-[85%] rounded-xl px-2.5 py-1.5 ${
                message.role === "user"
                  ? "bg-foreground text-background"
                  : "bg-muted text-foreground"
              }`}
              style={{ fontSize: 10.5, lineHeight: 1.5 }}
            >
              {message.text}
            </div>
          </div>
        ))}
        {historyLoading && <LoadingState />}
        {historyError ? (
          <div className="flex">
            <div
              className="max-w-[85%] rounded-xl bg-muted px-2.5 py-1.5 text-destructive"
              style={{ fontSize: 10.5, lineHeight: 1.5 }}
            >
              {historyError}
            </div>
          </div>
        ) : null}
        {sendError || requestError ? (
          <div className="flex">
            <div
              className="max-w-[85%] rounded-xl bg-muted px-2.5 py-1.5 text-destructive"
              style={{ fontSize: 10.5, lineHeight: 1.5 }}
            >
              {sendError || requestError}
            </div>
          </div>
        ) : null}
        {sending || requestPending ? (
          <div className="flex">
            <div
              className="max-w-[85%] rounded-xl bg-muted px-2.5 py-1.5 text-muted-foreground"
              style={{ fontSize: 10.5, lineHeight: 1.5 }}
            >
              {pendingLabel}
            </div>
          </div>
        ) : null}
      </div>
      <div className="border-t border-border/60">
        {contextLabel ? (
          <div className="px-2 pt-2">
            <div
              aria-label={`Selected cells: ${contextLabel}`}
              className="flex items-center gap-1.5 rounded-lg border border-border bg-muted/60 px-2 py-1.5"
            >
              <span
                className="shrink-0 text-muted-foreground"
                style={{ fontSize: 10 }}
              >
                Selected
              </span>
              <span
                className="min-w-0 flex-1 truncate font-medium text-foreground"
                title={contextLabel}
                style={{ fontSize: 10.5 }}
              >
                {contextLabel}
              </span>
              {onClearContext ? (
                <button
                  type="button"
                  aria-label="Clear selected cells"
                  onClick={onClearContext}
                  className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-background hover:text-foreground"
                >
                  <X aria-hidden="true" size={11} />
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
        <form
          className="flex items-end gap-1.5 p-2"
          onSubmit={(event) => {
            event.preventDefault();
            send();
          }}
        >
          <textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                void send();
              }
            }}
            placeholder={placeholder}
            disabled={inputDisabled}
            rows={variant === "inline" ? 2 : 1}
            className="flex-1 resize-none rounded-lg bg-muted px-2.5 py-2 outline-none"
            style={{ fontSize: 11 }}
          />
          <button
            type="submit"
            disabled={!draft.trim() || inputDisabled}
            className="rounded-lg bg-foreground px-2.5 py-2 font-medium text-background disabled:bg-muted disabled:text-muted-foreground"
            style={{ fontSize: 11 }}
          >
            Send
          </button>
        </form>
      </div>
    </>
  );

  if (variant === "inline") {
    return (
      <section className="mt-6 overflow-hidden rounded-xl border border-border bg-card">
        {chat}
      </section>
    );
  }

  if (variant === "docked") {
    return (
      <div className="relative flex shrink-0 justify-end border-t border-border bg-card px-5 py-3">
        {launcher}
        {open && (
          <aside
            aria-label={title}
            className="absolute bottom-full right-5 z-40 mb-3 flex h-[380px] max-h-[calc(100dvh-100px)] w-[320px] max-w-[calc(100vw-40px)] flex-col rounded-xl border border-border bg-card shadow-2xl"
          >
            {chat}
          </aside>
        )}
      </div>
    );
  }

  return (
    <aside
      aria-label={title}
      className="fixed bottom-5 right-5 z-40 flex h-[380px] w-[320px] flex-col rounded-xl border border-border bg-card shadow-2xl"
    >
      {chat}
    </aside>
  );
}
